import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Container,
  IconButton,
  CircularProgress,
} from "@mui/material";
// import CloudUploadIcon from "../../assets/icons/upload.svg";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description"; // Applicant
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"; // Sponsor
import { useSelector } from "react-redux";
import LoginQRModal from "../../components/LoginQRModal ";
import { getUserDocuments, userUpload } from "../../services/UserUploadService";
import { showErrorToast, showInfoToast, showToast } from "../../components/common/Toast";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { CustomButton } from "../../utils/CustomTagComponents";

const uploadSections = [
  {
    title: "Applicant's Documents",
    fields: [
      { id: "letter", label: "Admission Letter / Offer Letter", optional: true }, // ✅ only optional
      { id: "aadhar", label: "Aadhar Card", required: true },
      { id: "pan", label: "PAN Card", required: true },
      { id: "degree", label: "Degree Certificate", required: true },
      { id: "transcript", label: "Transcript", required: true },
      { id: "marksheet", label: "12th Marksheet", required: true },
    ],
  },
  {
    title: "Sponsor's Documents",
    fields: [
      { id: "sponsor_pan", label: "PAN Card", required: true },
      { id: "sponsor_aadhaar", label: "Aadhar Card", required: true },
      { id: "salaryslip", label: "Last 6 Months' Salary Slips", required: true },
      { id: "itr", label: "Last 3 Years ITR", required: true },
      { id: "bank_statement", label: "Last 6 Months Bank Statement", required: true },
      { id: "employment_proof", label: "Employment Proof / Business Proof", required: true },
    ],
  },
];

const FinanceSupportForm = () => {

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };
  const [sectionRef, sectionInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { auth } = useSelector((state) => state.auth || {});
  const isLoggedIn = Boolean(auth?.idToken || auth?.localId || auth?.email);

  // Store files for each field as array
  const [files, setFiles] = useState({});
  const [loadingFiles, setLoadingFiles] = useState({});
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [removedFields, setRemovedFields] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!isLoggedIn) return;
      setLoadingDocs(true);

      try {
        const res = await getUserDocuments(); // call your GET API
        if (res.data.success && res.data.data) {
          const preloaded = {};
          Object.entries(res.data.data).forEach(([type, docs]) => {
            preloaded[type] = docs.map(doc => ({
              name: doc.original_name,
              url: doc.file_path,
            }));
          });
          setFiles(preloaded);
        }
      } catch (err) {
        console.error("Failed to fetch documents:", err);
      } finally {
        setLoadingDocs(false);
      }
    };

    fetchDocuments();
  }, [isLoggedIn]);

  const handleFileChange = (e, fieldId) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileObj = {
      file,
      name: file.name,
      url: URL.createObjectURL(file),
    };

    // ✅ add file
    setFiles((prev) => ({
      ...prev,
      [fieldId]: [fileObj],
    }));

    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });

    // ✅ REMOVE from removedFields if exists
    setRemovedFields((prev) => prev.filter((f) => f !== fieldId));
  };
  const truncateFileName = (name, maxLength = 20) => {
    if (!name) return "";
    return name.length > maxLength ? name.substring(0, maxLength) + "..." : name;
  };

  const handleUploadClick = (fieldId) => {
    if (isLoggedIn) {
      const input = document.getElementById(fieldId);
      if (input) {
        input.value = ""; // reset previous selection
        input.click();
      }
    } else {
      setLoginModalOpen(true);
    }
  };

  const handleSubmit = async () => {
    if (!isLoggedIn) {
      setLoginModalOpen(true);
      return;
    }

    let newErrors = {};

    uploadSections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.required && !files[field.id]?.length) {
          newErrors[field.id] = true;
        }
      });
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showErrorToast("Please upload all mandatory documents");
      return;
    }

    setErrors({}); // clear errors

    // ✅ CHECK: at least one file uploaded OR removed
    const hasFiles =
      Object.values(files).some((arr) => arr?.length > 0);

    const hasRemoved = removedFields.length > 0;

    if (!hasFiles && !hasRemoved) {
      showInfoToast("Please upload at least one document");
      return; // ❌ STOP API CALL
    }

    const formData = new FormData();

    // ✅ ADD FILES
    Object.entries(files).forEach(([key, fileArr]) => {
      fileArr.forEach((f) => {
        if (f.file) formData.append(key, f.file);
      });
    });

    // ✅ ADD REMOVED FIELDS (only if not re-uploaded)
    removedFields.forEach((field) => {
      if (!files[field]) {
        formData.append(field, null);
      }
    });

    try {
      setSubmitting(true);
      const res = await userUpload(formData);

      if (res.data.success) {
        showToast("Documents updated successfully");
        setRemovedFields([]);
        await getUserDocuments();
      } else {
        showErrorToast(res.data.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      showErrorToast("Upload failed, please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveFile = (fieldId) => {
    setFiles((prev) => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });

    setRemovedFields((prev) => [...new Set([...prev, fieldId])]); // ✅ track removed
  };

  return (
    <Box
      component={motion.div}
      ref={sectionRef}
      variants={containerVariants}
      initial="hidden"
      animate={sectionInView ? "visible" : "hidden"}
      sx={{ bgcolor: "#f8fafc", my: { xs: 2, sm: 2, md: 3 } }}>
      <Container sx={{
        py: { xs: 2, sm: 2, md: 3 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" }
      }}>
        <Box sx={{ textAlign: "center", mb: 5 }}>


          {/* TITLE */}
          <Typography
            variant="h3"
            sx={{
              color: "black",
              fontWeight: 700,
              fontSize: { xs: "26px", md: "36px" },
              mb: 2,
            }}
          >
            Check Your Loan Eligibility
          </Typography>

          {/* SUBTITLE */}
          <Typography
            sx={{
              color: "black",
              fontSize: "15px",
              mx: "auto",
              lineHeight: 1.2,
            }}
          >
            Upload the required documents to get a quick assessment of your education loan eligibility. Our team will review and guide you with the best options.
          </Typography>
        </Box>


        {loadingDocs ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          uploadSections.map((section, sectionIndex) => (
            <Box
              key={sectionIndex}
              sx={{
                p: 3,
                borderRadius: "16px",
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                mb: sectionIndex === uploadSections.length - 1 ? 2.5 : 4,
              }}
            >

              {/* 🔹 SECTION HEADING */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                }}
              >
                {/* ICON BOX */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {section.title === "Applicant's Documents" ? (
                    <DescriptionIcon sx={{ color: "black", fontSize: 20 }} />
                  ) : (
                    <AccountBalanceIcon sx={{ color: "black", fontSize: 20 }} />
                  )}
                </Box>

                {/* TEXT CONTENT */}
                <Box>
                  <Typography
                    sx={{
                      color: "black",
                      fontWeight: 600,
                      fontSize: "18px",
                      lineHeight: 1.2,
                    }}
                  >
                    {section.title}
                  </Typography>

                  {/* ✅ SUBTITLE */}
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: "13px",
                      mt: "2px",
                    }}
                  >
                    {section.title === "Applicant's Documents"
                      ? "Upload your personal academic documents"
                      : "Upload your sponsor's financial documents"}
                  </Typography>
                </Box>
              </Box>

              {/* 🔹 GRID */}
              <Grid container spacing={3}>
                {section.fields.map((field) => (
                  <Grid
                    key={field.id}
                    size={{ xs: 12, sm: 6, md: 4 }} // ✅ 3 column
                  >

                    <Box
                      onClick={() => handleUploadClick(field.id)}
                      sx={{
                        height: "70px",
                        borderRadius: "14px",
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        transition: "0.3s",

                        background: "#f9fafb",
                        border: errors[field.id]
                          ? "1px solid #ef4444"
                          : files[field.id]?.length
                            ? "1px solid #22c55e"
                            : "1px solid #e2e8f0",

                        "&:hover": {
                          border: "1px solid #3b82f6",
                          background: "#f1f5f9",
                        }
                      }}
                    >
                      {/* ICON BOX */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#e2e8f0",
                          color: "black",
                        }}
                      >
                        <FileUploadIcon
                          sx={{
                            color: files[field.id]?.length ? "#22c55e" : "black",
                          }}
                        />
                      </Box>

                      {/* TEXT */}
                      <Box sx={{ flex: 1, overflow: "hidden" }}>
                        <Typography
                          sx={{
                            color: "#334155",
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {files[field.id]?.length
                            ? truncateFileName(files[field.id][0].name, 30)
                            : `${field.label}${field.required ? " *" : ""}`}
                        </Typography>

                      </Box>

                      {/* REMOVE */}
                      {files[field.id]?.length && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(field.id);
                          }}
                          sx={{ color: "#ef4444" }}
                        >
                          <CloseIcon />
                        </IconButton>
                      )}

                      <input
                        type="file"
                        id={field.id}
                        hidden
                        onChange={(e) => handleFileChange(e, field.id)}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        )}

        <Typography
          sx={{
            fontSize: "13px",
            textAlign: 'center',
            color: "#64748b",
          }}
        >
          <span style={{ color: "#ef4444" }}>*</span> Indicates mandatory fields
        </Typography>


        <Box
          component={motion.div}
          variants={fadeUp}
          sx={{ textAlign: "center", mt: 2 }}>
          <CustomButton
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} sx={{ color: "black" }} /> : "Submit Documents"}
          </CustomButton>
        </Box>
      </Container>

      {loginModalOpen && (
        <LoginQRModal open={loginModalOpen} handleClose={() => setLoginModalOpen(false)} />
      )}
    </Box>
  );
};

export default FinanceSupportForm;